// externals
import type { Reducer } from 'redux';

// libraries
import { PushToFIStatus, type PushToFIProviderState, type PushToFIState } from '@makemydeal/dr-dash-types';
import {
    CLEAR_PUSH_TO_FI_CACHE,
    CONFIRM_PUSH_TO_FI,
    PUSH_TO_FI_DEAL_REF_ID_TIMEOUT_EVENT,
    PUSH_TO_FI_FAILURE,
    PUSH_TO_FI_OFFER_SAVE_FAILURE_EVENT,
    PUSH_TO_FI_SUCCESS,
    UPDATE_PUSH_TO_FI
} from '../actionTypes/pushToFIActionTypes';
import { initActionTypes } from '@makemydeal/dr-shared-store';

const { INIT_PENCIL_SUCCESS } = initActionTypes;

export const pushToFIProviderInitialState: PushToFIProviderState = {
    timestamp: '',
    bffResult: { message: '', ok: false },
    wsResult: { message: '', ok: false },
    status: PushToFIStatus.Ready
};

export const pushToFIInitialState: PushToFIState = {};

export type PushToFIReducer = Reducer<PushToFIState>;

export const reducer: PushToFIReducer = (state: PushToFIState = pushToFIInitialState, action: any) => {
    switch (action.type) {
        case INIT_PENCIL_SUCCESS: {
            const { pushToFI } = action.payload;
            return { ...state, ...pushToFI };
        }
        case CONFIRM_PUSH_TO_FI: {
            const { providerId, correlationId, initiatedAt } = action.payload;
            return {
                ...state,
                [providerId]: {
                    ...pushToFIProviderInitialState,
                    status: PushToFIStatus.Waiting,
                    correlationId,
                    initiatedAt
                }
            };
        }
        case UPDATE_PUSH_TO_FI: {
            const { providerId, timestamp, message, ok, status } = action.payload;
            return {
                ...state,
                [providerId]: {
                    ...pushToFIProviderInitialState,
                    ...state[providerId],
                    timestamp,
                    wsResult: { message, ok },
                    status
                }
            };
        }
        case PUSH_TO_FI_SUCCESS: {
            const providerId = action.meta?.providerId;
            if (!providerId) return state;
            const message = action.payload?.message ?? '';
            return {
                ...state,
                [providerId]: {
                    ...pushToFIProviderInitialState,
                    ...state[providerId],
                    bffResult: { message, ok: true },
                    status: PushToFIStatus.Waiting
                }
            };
        }
        case PUSH_TO_FI_FAILURE: {
            const providerId = action.meta?.providerId;
            if (!providerId) return state;
            const message = action.payload?.message ?? action.payload?.statusText ?? 'Push to FI failed';
            return {
                ...state,
                [providerId]: {
                    ...pushToFIProviderInitialState,
                    ...state[providerId],
                    bffResult: { message, ok: false },
                    status: PushToFIStatus.Failure
                }
            };
        }
        case PUSH_TO_FI_OFFER_SAVE_FAILURE_EVENT: {
            const { providerId, message, ok } = action.payload;
            return {
                ...state,
                [providerId]: {
                    ...pushToFIProviderInitialState,
                    ...state[providerId],
                    bffResult: { message, ok },
                    status: PushToFIStatus.OfferSaveFailure
                }
            };
        }
        case PUSH_TO_FI_DEAL_REF_ID_TIMEOUT_EVENT: {
            const { providerId, message, ok } = action.payload;
            return {
                ...state,
                [providerId]: {
                    ...pushToFIProviderInitialState,
                    ...state[providerId],
                    bffResult: { message, ok },
                    status: PushToFIStatus.Failure
                }
            };
        }
        case CLEAR_PUSH_TO_FI_CACHE: {
            const { providerId } = action.payload;
            return {
                ...state,
                [providerId]: {
                    ...pushToFIProviderInitialState,
                    ...state[providerId],
                    bffResult: { message: '', ok: false },
                    wsResult: { message: '', ok: false },
                    status: PushToFIStatus.Ready
                }
            };
        }
        default: {
            return state;
        }
    }
};
